import { useState } from 'react';
import { useSelector } from 'react-redux';
import { scale, width } from '@components/frame';
import { isPhone, ncFileStatuses } from '@helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { getProductiveTimeBlock } from '@components/machines/components/detailsComponents/activeMachine/helpers';
import { useFilterTimeCategoriesData } from '@v2/pages/common/hooks/useFilterTimeCategoriesData';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { selectFeatureFlags } from '@reducers/user';
import { TimeCategory } from '@interfaces/base';
import type { BatchProps } from '@components/machines/components/detailsComponents/activeMachine/components/Batch/Batch';

export interface UseManageBatchParams
  extends Pick<BatchProps, 'C' | 'detailsWidth' | 'maxDetailsWidth' | 'batch' | 'batchLoading' | 'selectedItem' | 'bg'> {}

export const useManageBatch = (params: UseManageBatchParams) => {
  const { C, detailsWidth, maxDetailsWidth, batch, batchLoading, selectedItem, bg } = params;

  const featureFlags = useSelector(selectFeatureFlags) as Record<string, boolean>;

  const {
    links,
    scheduledSetupTimeInSeconds = 0,
    actualSetupTimeInSeconds = 0,
    actualChangeTimeTotalInSeconds = 0,
    actualActiveTimeTotalInSeconds = 0,
    scheduledActiveTimeTotalInSeconds = 0,
    scheduledActiveTimeAverageInSeconds = 0,
    scheduledChangeTimeAverageInSeconds = 0,
    scheduledChangeTimeTotalInSeconds = 0,
    scheduledTotalTimeTotalInSeconds = 0,
    paidActiveTimeAverageInSeconds,
    paidActiveTimeTotalInSeconds,
    paidChangeTimeAverageInSeconds,
    paidChangeTimeTotalInSeconds,
    paidSetupTimeInSeconds = 0,
    idealTotalTimeTotalInSeconds = 0,
    idealActiveTimeTotalInSeconds,
    idealActiveTimeAverageInSeconds,
    idealChangeTimeTotalInSeconds,
    idealChangeTimeAverageInSeconds,
    idealSetupTimeInSeconds = 0,
    actualActiveTimeAverageInSeconds,
    p25ActiveTimeInSeconds,
    p50ActiveTimeInSeconds,
    p75ActiveTimeInSeconds,
    actualChangeTimeAverageInSeconds,
    p25ChangeTimeInSeconds,
    p50ChangeTimeInSeconds,
    p75ChangeTimeInSeconds,
    quantityReady,
  } = batch || {};

  const [showPerPcs, setShowPerPcs] = useState(false);

  const { safeIsMachinesScreenCheck } = useNavigationCheck();

  const padding = scale(18);

  const containWidth = isPhone ? width : Math.min(maxDetailsWidth, detailsWidth) - padding * 2;

  const chartContainPadding = scale(18);
  const chartWidth = containWidth - chartContainPadding * 2 - (isPhone ? 30 : 0);

  const isQuantityValid = quantityReady != null && quantityReady >= 3;

  const showChartPercentiles = featureFlags['batch-showpercentiles'] && isQuantityValid && showPerPcs;

  const roundTopConfig = {
    borderRadius: scale(SCREEN_BORDER_RADIUS),
  };

  const ncFileStatus = safeIsMachinesScreenCheck ? selectedItem?.machineStatus?.ncFileStatus : batch?.ncFileStatus;

  const productiveTimeBlock = getProductiveTimeBlock({ C, batch, ncFileStatus });

  const setupTimeData = [
    { name: TimeCategory.ACTUAL, value: actualSetupTimeInSeconds, color: 'Time/Light/Actual', icon: 'actual', width: 14, height: 14 },
    {
      name: TimeCategory.SCHEDULED,
      value: scheduledSetupTimeInSeconds,
      color: 'Selected/Light/Primary',
      icon: 'scheduledTime',
      width: 14,
      height: 14,
    },
    { name: TimeCategory.IDEAL, value: idealSetupTimeInSeconds, color: 'Label/Light/Tertiary', icon: 'runningBunny', width: 14, height: 18 },
    { name: TimeCategory.PAID, value: paidSetupTimeInSeconds, color: 'Status/Light/Paid', icon: 'euro', width: 10, height: 18 },
  ];

  const activeTimeData = [
    {
      name: TimeCategory.ACTUAL,
      value: showPerPcs ? actualActiveTimeAverageInSeconds : actualActiveTimeTotalInSeconds,
      color: 'Time/Light/Actual',
      icon: 'actual',
      width: 14,
      height: 14,
    },
    {
      name: TimeCategory.SCHEDULED,
      value: showPerPcs ? scheduledActiveTimeAverageInSeconds : scheduledActiveTimeTotalInSeconds,
      color: 'Selected/Light/Primary',
      icon: 'scheduledTime',
      width: 14,
      height: 14,
    },
    {
      name: TimeCategory.IDEAL,
      value: showPerPcs ? idealActiveTimeAverageInSeconds : idealActiveTimeTotalInSeconds,
      color: 'Label/Light/Tertiary',
      icon: 'runningBunny',
      width: 14,
      height: 18,
    },
    {
      name: TimeCategory.PAID,
      value: showPerPcs ? paidActiveTimeAverageInSeconds : paidActiveTimeTotalInSeconds,
      color: 'Status/Light/Paid',
      icon: 'euro',
      width: 10,
      height: 18,
    },
  ];

  const changeTimeData = [
    {
      name: TimeCategory.ACTUAL,
      value: showPerPcs ? actualChangeTimeAverageInSeconds : actualChangeTimeTotalInSeconds,
      color: 'Time/Light/Actual',
      icon: 'actual',
      width: 14,
      height: 14,
    },
    {
      name: TimeCategory.SCHEDULED,
      value: showPerPcs ? scheduledChangeTimeAverageInSeconds : scheduledChangeTimeTotalInSeconds,
      color: 'Selected/Light/Primary',
      icon: 'scheduledTime',
      width: 14,
      height: 14,
    },
    {
      name: TimeCategory.IDEAL,
      value: showPerPcs ? idealChangeTimeAverageInSeconds : idealChangeTimeTotalInSeconds,
      color: 'Label/Light/Tertiary',
      icon: 'runningBunny',
      width: 14,
      height: 18,
    },
    {
      name: TimeCategory.PAID,
      value: showPerPcs ? paidChangeTimeAverageInSeconds : paidChangeTimeTotalInSeconds,
      color: 'Status/Light/Paid',
      icon: 'euro',
      width: 10,
      height: 18,
    },
  ];

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const commonFilterParams = {
    ncFileStatus,
    batch,
  };

  const filteredProductiveTimeData = useFilterTimeCategoriesData({ ...commonFilterParams, skipItems: 2, data: productiveTimeBlock });
  const filteredSetupTimeData = useFilterTimeCategoriesData({ ...commonFilterParams, data: setupTimeData });
  const filteredActiveTimeData = useFilterTimeCategoriesData({ ...commonFilterParams, data: activeTimeData });
  const filteredChangeTimeData = useFilterTimeCategoriesData({ ...commonFilterParams, data: changeTimeData });

  const toggleShowPerPcs = () => {
    setShowPerPcs(!showPerPcs);
  };

  return {
    showPerPcs,
    toggleShowPerPcs,
    isQuantityValid,
    showChartPercentiles,
    roundTopConfig,
    shadowStyles,
    chartWidth,
    quantityReady,
    actualSetupTimeInSeconds,
    paidSetupTimeInSeconds,
    idealSetupTimeInSeconds,
    p25ActiveTimeInSeconds,
    p50ActiveTimeInSeconds,
    p75ActiveTimeInSeconds,
    p25ChangeTimeInSeconds,
    p50ChangeTimeInSeconds,
    p75ChangeTimeInSeconds,
    filteredProductiveTimeData,
    filteredSetupTimeData,
    filteredActiveTimeData,
    filteredChangeTimeData,
  };
};
